package com.evoapps.evoshift.domain.usecase.access

import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.AccessProblem
import com.evoapps.evoshift.domain.model.AccessRefusedException
import com.evoapps.evoshift.domain.model.AccessRoles
import com.evoapps.evoshift.domain.model.CustomRole
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.repository.AccessRepository

/** SH-270: the business's own roles, by name. */
class GetRolesUseCase(private val repository: AccessRepository) {
    suspend operator fun invoke(ownerId: String): Result<List<CustomRole>> =
        repository.getRoles(ownerId).map { roles -> roles.sortedBy { it.name.lowercase() } }
}

/**
 * SH-270: creates or changes a role of the owner's business after the checks the server makes too
 * (a name of 1-40 characters, unique in the business), so the editor says what is wrong at once.
 */
class SaveRoleUseCase(private val repository: AccessRepository) {
    suspend operator fun invoke(roleId: String?, name: String, levels: Map<Feature, AccessLevel>, others: List<CustomRole>): Result<String> =
        runCatching {
            val trimmed = name.trim()
            if (trimmed.isEmpty() || trimmed.length > AccessRoles.MAX_NAME) throw AccessRefusedException(AccessProblem.MALFORMED)
            if (others.any { it.id != roleId && it.name.trim().equals(trimmed, ignoreCase = true) }) {
                throw AccessRefusedException(AccessProblem.NAME_TAKEN)
            }
            repository.saveRole(roleId, trimmed, AccessRoles.effective(levels, emptyMap())).getOrThrow()
        }
}

/** SH-270: deletes a role nobody has (the server refuses one in use: ROLE_IN_USE). */
class DeleteRoleUseCase(private val repository: AccessRepository) {
    suspend operator fun invoke(roleId: String): Result<Unit> = repository.deleteRole(roleId)
}

/**
 * SH-270: gives a person a role and modifications at a workplace ("Reset to role": no
 * modifications). Only the modifications that differ from the role are sent.
 */
class SetEngagementAccessUseCase(private val repository: AccessRepository) {
    suspend operator fun invoke(
        userId: String,
        workplaceId: String,
        roleId: String,
        roleLevels: Map<Feature, AccessLevel>,
        overrides: Map<Feature, AccessLevel>
    ): Result<Unit> = repository.setEngagementAccess(userId, workplaceId, roleId, AccessRoles.meaningful(roleLevels, overrides))
}
